/**
 * Smart tooltip — opt-in, reusable anywhere:
 *
 *   import '../../utils/smartTooltip';
 *   <button className="smart-tooltip" data-tooltip="Text to show">…</button>
 *
 * One tooltip element lives on <body>, so no scrolling list, card or modal
 * with overflow can clip it (the CSS-only .avatar-tooltip is drawn inside its
 * trigger and gets cut off at those edges). It prefers sitting above the
 * trigger, flips below when there isn't room above, and slides sideways to
 * stay on screen while its arrow keeps pointing at the trigger.
 *
 * Importing this module installs the listeners once; importing it again
 * anywhere else is a no-op. An empty data-tooltip shows nothing.
 */
// Styles: .smart-tooltip-bubble in styles/App.css, next to .avatar-tooltip.

const EDGE = 8; // keep this far from the viewport edges
const GAP = 8;  // space between trigger and tooltip (room for the arrow)
const TRIGGER = '.smart-tooltip[data-tooltip]';

let bubble = null;
let current = null;

const ensureBubble = () => {
  if (bubble) return bubble;
  bubble = document.createElement('div');
  bubble.className = 'smart-tooltip-bubble';
  bubble.setAttribute('role', 'tooltip');
  document.body.appendChild(bubble);
  return bubble;
};

const hide = () => {
  current = null;
  if (bubble) bubble.classList.remove('is-visible');
};

const show = (target) => {
  const text = target.getAttribute('data-tooltip');
  if (!text) {
    hide();
    return;
  }
  current = target;
  const el = ensureBubble();
  el.textContent = text;
  el.classList.remove('is-below', 'is-visible');

  // Measure at its real size before placing it.
  el.style.left = '0px';
  el.style.top = '0px';
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  const r = target.getBoundingClientRect();

  const fitsAbove = r.top - GAP - h >= EDGE;
  const fitsBelow = r.bottom + GAP + h <= window.innerHeight - EDGE;
  const below = !fitsAbove && fitsBelow;
  const top = below ? r.bottom + GAP : r.top - GAP - h;

  const center = r.left + r.width / 2;
  const left = Math.min(Math.max(center - w / 2, EDGE), Math.max(window.innerWidth - EDGE - w, EDGE));

  el.style.left = `${left}px`;
  el.style.top = `${Math.max(top, EDGE)}px`;
  // Arrow follows the trigger even when the box has slid sideways.
  el.style.setProperty('--smart-tooltip-arrow-x', `${Math.min(Math.max(center - left, 12), w - 12)}px`);
  el.classList.toggle('is-below', below);
  el.classList.add('is-visible');
};

const install = () => {
  if (typeof window === 'undefined' || window.__smartTooltipInstalled) return;
  window.__smartTooltipInstalled = true;

  document.addEventListener('mouseover', (e) => {
    const target = e.target.closest ? e.target.closest(TRIGGER) : null;
    if (target === current) return;
    if (target) show(target);
    else hide();
  });
  document.addEventListener('mouseout', (e) => {
    if (!current) return;
    if (e.relatedTarget && current.contains(e.relatedTarget)) return;
    hide();
  });
  // Keyboard users get it on focus, like hover.
  document.addEventListener('focusin', (e) => {
    const target = e.target.closest ? e.target.closest(TRIGGER) : null;
    if (target) show(target);
  });
  document.addEventListener('focusout', hide);
  // The trigger moves or goes away: don't leave the tooltip floating.
  document.addEventListener('click', hide, true);
  window.addEventListener('scroll', hide, true);
  window.addEventListener('resize', hide);
};

install();
